// src/features/status/components/StatusPage.tsx
//
// «Status page (ingest gaps per source)» (w7.u5, D30/B4: "Insight declares
// its own delivery guarantee ... an ingest gap register per source exposed
// on the status page") — route /status. `GET /api/v2/ui/status` (w7.u1)
// lists every source that covers the requested property with its gap count
// by counter over the window — a raw source-row shape, not a `MetricResultDto`
// list, so this page renders its own table rather than `MetricPanelSection`.
//
// This is the **authenticated, property-scoped** status page
// (`CanViewV2PropertyAsync`, same gate as the rest of this unit's screens).
// A **public, anonymous** status page is a separate authorization model the
// controller's own doc comment leaves to Cris (commercial-fit.md line 133) —
// not built here, stated rather than guessed at.
import React from 'react';
import {
  Box, Chip, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import { useDataProvider, useTranslate } from 'react-admin';
import { SectionHeader } from '@/components/common/SectionHeader';
import { FetchErrorBanner } from '@/components/common/FetchErrorBanner';
import { EmptyState } from '@/components/common/EmptyState';
import { WidgetCard } from '@/components/common/WidgetCard';
import { PeriodPicker, defaultPeriod, type PeriodValue } from '@/components/common/PeriodPicker';
import { useV2PropertySelection } from '@/hooks/useV2PropertySelection';
import { useAnalyticsFetch } from '@/hooks/useAnalyticsFetch';
import { PropertySelector } from '@/features/insight-v2-shared/components/PropertySelector';
import { PropertyState } from '@/features/insight-v2-shared/components/PropertyState';
import { DASH, formatCount } from '@/lib/analyticsFormat';
import type { StatusResponse, StatusSourceRow } from '@/types/analyticsV2';
import { localIsoDate, localIsoDateDaysAgo } from '@/lib/localDate';
import { deliveryClassKey, sourceKindKey, sourceStatusColor, sourceStatusKey, statusWindow } from '../statusLabels';


const SourceRow: React.FC<{ row: StatusSourceRow }> = ({ row }) => {
  const translate = useTranslate();
  const counters = Object.entries(row.gapsByCounter);
  return (
    <TableRow>
      <TableCell>{row.source}</TableCell>
      <TableCell>{translate(sourceKindKey(row.kind))}</TableCell>
      <TableCell>{translate(deliveryClassKey(row.deliveryClass))}</TableCell>
      <TableCell>
        <Chip size="small" label={translate(sourceStatusKey(row.status))} color={sourceStatusColor(row.status)} />
      </TableCell>
      <TableCell align="right">{formatCount(row.totalGaps)}</TableCell>
      <TableCell>
        {counters.length === 0 ? (
          <Typography variant="body2" color="text.secondary">{DASH}</Typography>
        ) : (
          counters.map(([counter, count]) => (
            <Typography key={counter} variant="caption" sx={{ display: 'block' }}>
              {counter}: {formatCount(count)}
            </Typography>
          ))
        )}
      </TableCell>
    </TableRow>
  );
};

export const StatusPage: React.FC = () => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const property = useV2PropertySelection('status');

  const { propertyId } = property;
  // Until the person picks a period the page asks for the last 48 hours ending
  // now (help: «las últimas 48 horas»); the picker shows those days meanwhile.
  const [period, setPeriod] = React.useState<PeriodValue>(() => ({
    ...defaultPeriod(2),
    from: localIsoDateDaysAgo(2),
    to: localIsoDate(),
  }));
  const [picked, setPicked] = React.useState(false);
  const handlePeriod = (value: PeriodValue) => {
    setPeriod(value);
    setPicked(true);
  };

  const { data, loading, error, refresh } = useAnalyticsFetch<StatusResponse>(
    () => (dataProvider as any).getAnalyticsStatus({ property: propertyId, ...statusWindow(picked ? period : null) }),
    [dataProvider, propertyId, period.from, period.to, picked],
    Boolean(propertyId)
  );

  return (
    <Box>
      <SectionHeader manualScreen="status"
        left={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <PropertySelector selection={property} />
            <PeriodPicker value={period} onChange={handlePeriod} showCompare={false} />
          </Box>
        }
        subtitle={translate('resources.status.subtitle')}
        helpConfig={{
          titleKey: 'resources.status.help.title',
          descriptionKey: 'resources.status.help.description',
          featureKeys: ['resources.status.help.features.list'],
          tipKeys: ['resources.status.help.tips.property'],
        }}
      />

      {!propertyId ? (
        <PropertyState selection={property} />
      ) : (
        <>
          {error && <FetchErrorBanner onRetry={refresh} />}
          <WidgetCard title={translate('resources.status.table.title')}>
            {loading ? (
              <Typography variant="body2" color="text.secondary">{DASH}</Typography>
            ) : !data || data.sources.length === 0 ? (
              <EmptyState message={translate('resources.status.table.empty')} minHeight={160} />
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{translate('resources.status.table.source')}</TableCell>
                    <TableCell>{translate('resources.status.table.kind')}</TableCell>
                    <TableCell>{translate('resources.status.table.deliveryClass')}</TableCell>
                    <TableCell>{translate('resources.status.table.status')}</TableCell>
                    <TableCell align="right">{translate('resources.status.table.totalGaps')}</TableCell>
                    <TableCell>{translate('resources.status.table.gapsByCounter')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.sources.map((row) => (
                    <SourceRow key={row.source} row={row} />
                  ))}
                </TableBody>
              </Table>
            )}
          </WidgetCard>
        </>
      )}
    </Box>
  );
};

export default StatusPage;
